import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';

interface IInvoiceContractorPayBreakdownReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
}

type ReportType = 'CPOB' | 'CPOBPP' | 'ICPOB' | 'IBOC' | 'IOS';

// Explicit useForm() generic - passing a narrower inline defaultValues object (just
// reportType/filename) makes react-hook-form infer getValues()/watch()'s type from that literal
// alone, so every other registered field (begDate/endDate/type/office) reads as a TS2339 error.
interface IFormValues {
    reportType: ReportType;
    begDate: string;
    endDate: string;
    type: string;
    office: string;
    filename: string;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '10rem', textAlign: 'left', marginRight: '1rem' };

// icco_brkdwn.fmt's own Excel Report dialog offers 8 report types total (IccoExcelReportDialog.tsx,
// used by IccoBreakdownPage.tsx) - this dashboard entry only surfaces the 5 that don't already have
// their own dedicated dashboard entry elsewhere: Invoice Office Breakdown (IOB) is already under
// Billing ("Build Invoice Office Breakdown") and Invoice County Breakdown is a different report
// entirely, already under Accounts Receivable.
const REPORT_TYPE_OPTIONS: Array<{ value: ReportType; label: string }> = [
    { value: 'CPOB', label: 'Contractor Pay Office Breakdown' },
    { value: 'CPOBPP', label: 'Contractor Pay Office Breakdown - Pay Period' },
    { value: 'ICPOB', label: 'Invoice-Contractor Pay Office Breakdown' },
    { value: 'IBOC', label: 'Invoice Breakdown by Office and Client' },
    { value: 'IOS', label: 'Invoice State Breakdown' },
];

// Matches revenue.fmt/icco_brkdwn.fmt's own shared Type poplist (WIP Not Applied -> STD, WIP
// Applied -> WIP, WIP Only -> WIPONLY).
const TYPE_OPTIONS = [
    { value: 'STD', label: 'WIP Not Applied' },
    { value: 'WIP', label: 'WIP Applied' },
    { value: 'WIPONLY', label: 'WIP Only' },
];

// Only IOS's own stored proc (usp_InvoiceStateBreakdownReport) reads an @Office value among these
// 5 - confirmed per-report via its deployed RDL/proc, matching IccoExcelReportDialog's own
// REPORT_TYPES_WITH_OFFICE gating.
const REPORT_TYPES_WITH_OFFICE: ReadonlyArray<ReportType> = ['IOS'];

// Matches icco_brkdwn.fmt oid 76's own v_filename defaults per :excel.report value.
const DEFAULT_FILENAMES: Record<ReportType, string> = {
    CPOB: 'Contract_Pay_Office_Breakdown.xlsx',
    CPOBPP: 'Contract-Pay-Office-Breakdown-By-PP.xlsx',
    ICPOB: 'Invoice-Contract_Pay_Office_Breakdown.xlsx',
    IBOC: 'Invoice_Breakdown_Office_Client.xlsx',
    IOS: 'Invoice_State_Breakdown.xlsx',
};

// Endpoint per report type. begDate/endDate are passed straight through as the native date input's
// own ISO (YYYY-MM-DD) value - each controller action binds them as DateTime query parameters, so
// .NET's own model binder parses them before any SQL-side date formatting comes into play (matching
// how IccoBreakdownPage.tsx's own buildReportUrl already feeds these same endpoints).
const buildReportUrl = (reportType: ReportType, type: string, begDate: string, endDate: string, office: string): string => {
    const base = new URLSearchParams({ type, begDate, endDate });
    switch (reportType) {
        case 'CPOB':
            return `/v1.0/reports/GetReport/ContractorPayOfficeBreakdown?${base.toString()}`;
        case 'CPOBPP':
            return `/v1.0/reports/GetReport/ContractorPayOfficeBreakdownPP?${base.toString()}`;
        case 'ICPOB':
            return `/v1.0/reports/GetReport/InvoiceContractorPayOfficeBreakdown?${base.toString()}`;
        case 'IBOC':
            return `/v1.0/reports/GetReport/InvoiceOfficeClientBreakdown?${base.toString()}`;
        case 'IOS':
            base.set('office', office ?? '');
            return `/v1.0/reports/GetReport/InvoiceStateBreakdown?${base.toString()}`;
        default:
            return '';
    }
};

const InvoiceContractorPayBreakdownReportDialog = (props: IInvoiceContractorPayBreakdownReportDialogProps) => {
    const { register, errors, watch, setValue, getValues } = useForm<IFormValues>({ defaultValues: { reportType: 'CPOB', filename: DEFAULT_FILENAMES.CPOB } });
    const { get: getReport, response: reportResponse } = useFetch();
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const reportType = watch('reportType') as ReportType;

    // Mirrors IccoExcelReportDialog.tsx's own reportType-driven filename default - RHFInputBox's
    // defaultVal only applies at mount (uncontrolled input), so switching Report needs an explicit
    // setValue to actually update the field rather than just re-evaluating a defaultVal prop.
    useEffect(() => {
        setValue('filename', DEFAULT_FILENAMES[reportType] ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportType]);

    const generateReport = async () => {
        const values = getValues();
        if (!values.begDate || !values.endDate) {
            setMessage('A beginning and ending date must be entered.');
            return;
        }

        setMessage('');
        setIsWorking(true);
        const url = buildReportUrl(values.reportType as ReportType, values.type || 'STD', values.begDate, values.endDate, values.office);
        await getReport(url);
        setIsWorking(false);

        if (!reportResponse.ok) {
            setMessage('Unable to generate the report. Please try again.');
            return;
        }

        if (reportResponse.status === 204) {
            setMessage('No data matched those filters.');
            return;
        }

        const blob = await reportResponse.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        const defaultName = DEFAULT_FILENAMES[values.reportType as ReportType];
        const baseName = (values.filename || defaultName).trim().replace(/\.xlsx$/i, '');
        a.download = `${baseName || defaultName}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        props.hideDialog();
    };

    return (
        <Dialog title="Invoice/Contractor Pay Breakdown Reports" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '32%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '85%' }}>
                    <RHFSelect
                        label="Report"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportType"
                        defaultVal="CPOB"
                        options={REPORT_TYPE_OPTIONS}
                        register={register}
                        style={{ width: '65%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="begDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={{ width: '65%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="endDate"
                        type="date"
                        register={register}
                        errors={errors}
                        style={{ width: '65%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="type"
                        defaultVal="STD"
                        options={TYPE_OPTIONS}
                        register={register}
                        style={{ width: '65%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    {REPORT_TYPES_WITH_OFFICE.includes(reportType) && (
                        <RHFInputBox
                            label="Office"
                            labelOnLeft={true}
                            labelStyle={labelStyle}
                            name="office"
                            register={register}
                            errors={errors}
                            style={{ width: '65%' }}
                            bodyStyle={{ width: '100%' }}
                        />
                    )}
                    <RHFInputBox
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="filename"
                        type="text"
                        defaultVal={DEFAULT_FILENAMES[reportType] ?? ''}
                        register={register}
                        errors={errors}
                        style={{ width: '65%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '1.5rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={isWorking ? 'Generating...' : 'Generate'}
                    loading={isWorking}
                    onClick={() => generateReport()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text="Cancel"
                    onClick={() => props.hideDialog()}
                />
            </div>
            {!!message && (
                <div style={{ color: 'red', textAlign: 'center', marginTop: '0.5rem' }}>{message}</div>
            )}
        </Dialog>
    );
};

export default InvoiceContractorPayBreakdownReportDialog;



// WEBPACK FOOTER //
// ./src/components/reports/dialogs/InvoiceContractorPayBreakdownReportDialog.tsx